import type { EncryptedBlob } from '@shared/types';
import { decryptWithMasterKey, encryptWithMasterKey } from '../crypto/keys';
import { openIndexedDb, requestToPromise, transactionDone } from '../utils/idb';
import {
  canUseNativeArchiveCacheStore,
  nativeArchiveCacheStore
} from '../utils/webviewBridge';

const DB_NAME = 'family-messenger-message-archive';
const DB_VERSION = 2;
const CACHE = 'archive_cache';

export type MessageArchiveCacheConversation = {
  conversationId: string;
  type: 'direct' | 'group';
  title: string;
  directThreadKey?: string;
  groupChatId?: string;
  participants: Array<{
    identityId: string;
    displayName?: string | null;
    role?: 'self' | 'peer' | 'participant';
  }>;
  messageCount: number;
  byteSize: number;
  firstMessageAt: string | null;
  lastMessageAt: string | null;
  latestPreview: string | null;
  periodKeys: string[];
};

export type MessageArchiveCacheIndex = {
  version: 1;
  sourceCircleIdentityId: string;
  archiveId: string;
  manifestRevision: number;
  periodCount: number;
  messageCount: number;
  byteSize: number;
  conversations: MessageArchiveCacheConversation[];
  updatedAt: string;
};

export type MessageArchiveCachePage = {
  version: 1;
  sourceCircleIdentityId: string;
  archiveId: string;
  conversationId: string;
  messages: Array<Record<string, unknown>>;
  periodKeys: string[];
  updatedAt: string;
};

type ArchiveCacheEntryType = 'index' | 'conversation_page';

type StoredArchiveCacheEntry = {
  cacheId: string;
  sourceCircleIdentityId: string;
  archiveId: string;
  entryType: ArchiveCacheEntryType;
  conversationId?: string | null;
  encryptedPayload: EncryptedBlob;
  updatedAt: string;
};

export function buildArchiveIndexCacheId(params: {
  sourceCircleIdentityId: string;
  archiveId: string;
}): string {
  return `${params.sourceCircleIdentityId.trim()}::${params.archiveId.trim()}::index`;
}

export function buildArchiveConversationPageCacheId(params: {
  sourceCircleIdentityId: string;
  archiveId: string;
  conversationId: string;
}): string {
  return `${params.sourceCircleIdentityId.trim()}::${params.archiveId.trim()}::conversation::${params.conversationId.trim()}`;
}

function openDb(): Promise<IDBDatabase> {
  return openIndexedDb(DB_NAME, DB_VERSION, (db) => {
    if (!db.objectStoreNames.contains('writer_checkpoints')) {
      const checkpoints = db.createObjectStore('writer_checkpoints', { keyPath: 'checkpointId' });
      checkpoints.createIndex('bySourceCircle', 'sourceCircleIdentityId');
      checkpoints.createIndex('byArchiveId', 'archiveId');
    }
    if (!db.objectStoreNames.contains(CACHE)) {
      const store = db.createObjectStore(CACHE, { keyPath: 'cacheId' });
      store.createIndex('byArchive', ['sourceCircleIdentityId', 'archiveId']);
      store.createIndex('byConversation', ['archiveId', 'conversationId']);
    }
  });
}

async function encryptCachePayload(masterKey: CryptoKey, value: unknown): Promise<EncryptedBlob> {
  return encryptWithMasterKey(masterKey, JSON.stringify(value));
}

async function decryptCachePayload<T>(masterKey: CryptoKey, blob: EncryptedBlob): Promise<T> {
  return JSON.parse(await decryptWithMasterKey(masterKey, blob)) as T;
}

async function getCacheEntry(cacheId: string): Promise<StoredArchiveCacheEntry | null> {
  if (canUseNativeArchiveCacheStore()) {
    const raw = nativeArchiveCacheStore.get(cacheId);
    if (raw) {
      try {
        return JSON.parse(raw) as StoredArchiveCacheEntry;
      } catch {
        // Fall through to IndexedDB.
      }
    }
  }

  const db = await openDb();
  try {
    const tx = db.transaction(CACHE, 'readonly');
    const row = await requestToPromise(tx.objectStore(CACHE).get(cacheId));
    return (row || null) as StoredArchiveCacheEntry | null;
  } finally {
    db.close();
  }
}

async function saveCacheEntry(entry: StoredArchiveCacheEntry): Promise<void> {
  if (canUseNativeArchiveCacheStore() && nativeArchiveCacheStore.save(entry.cacheId, JSON.stringify(entry))) {
    return;
  }

  const db = await openDb();
  try {
    const tx = db.transaction(CACHE, 'readwrite');
    tx.objectStore(CACHE).put(entry);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function saveArchiveIndex(params: {
  masterKey: CryptoKey;
  index: MessageArchiveCacheIndex;
}): Promise<void> {
  const cacheId = buildArchiveIndexCacheId(params.index);
  await saveCacheEntry({
    cacheId,
    sourceCircleIdentityId: params.index.sourceCircleIdentityId,
    archiveId: params.index.archiveId,
    entryType: 'index',
    conversationId: null,
    encryptedPayload: await encryptCachePayload(params.masterKey, params.index),
    updatedAt: new Date().toISOString()
  });
}

export async function getArchiveIndex(params: {
  masterKey: CryptoKey;
  sourceCircleIdentityId: string;
  archiveId: string;
}): Promise<MessageArchiveCacheIndex | null> {
  const entry = await getCacheEntry(buildArchiveIndexCacheId(params));
  if (!entry || entry.entryType !== 'index') return null;
  return decryptCachePayload<MessageArchiveCacheIndex>(params.masterKey, entry.encryptedPayload);
}

export async function saveArchiveConversationPage(params: {
  masterKey: CryptoKey;
  page: MessageArchiveCachePage;
}): Promise<void> {
  const cacheId = buildArchiveConversationPageCacheId(params.page);
  await saveCacheEntry({
    cacheId,
    sourceCircleIdentityId: params.page.sourceCircleIdentityId,
    archiveId: params.page.archiveId,
    entryType: 'conversation_page',
    conversationId: params.page.conversationId,
    encryptedPayload: await encryptCachePayload(params.masterKey, params.page),
    updatedAt: new Date().toISOString()
  });
}

export async function getArchiveConversationPage(params: {
  masterKey: CryptoKey;
  sourceCircleIdentityId: string;
  archiveId: string;
  conversationId: string;
}): Promise<MessageArchiveCachePage | null> {
  const entry = await getCacheEntry(buildArchiveConversationPageCacheId(params));
  if (!entry || entry.entryType !== 'conversation_page') return null;
  return decryptCachePayload<MessageArchiveCachePage>(params.masterKey, entry.encryptedPayload);
}

export async function clearArchiveCache(params: {
  sourceCircleIdentityId: string;
  archiveId: string;
}): Promise<void> {
  if (canUseNativeArchiveCacheStore()) {
    nativeArchiveCacheStore.deleteForArchive(params.sourceCircleIdentityId, params.archiveId);
  }

  const db = await openDb();
  try {
    const tx = db.transaction(CACHE, 'readwrite');
    const index = tx.objectStore(CACHE).index('byArchive');
    const rows = await requestToPromise(index.getAllKeys([params.sourceCircleIdentityId, params.archiveId])) as IDBValidKey[];
    for (const key of rows) tx.objectStore(CACHE).delete(key);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function clearArchiveCacheForIdentity(sourceCircleIdentityId: string): Promise<void> {
  if (canUseNativeArchiveCacheStore()) {
    nativeArchiveCacheStore.deleteForIdentity(sourceCircleIdentityId);
  }

  const db = await openDb();
  try {
    const tx = db.transaction(CACHE, 'readwrite');
    const store = tx.objectStore(CACHE);
    const request = store.openCursor();
    await new Promise<void>((resolve, reject) => {
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve();
          return;
        }
        const row = cursor.value as StoredArchiveCacheEntry;
        if (row.sourceCircleIdentityId === sourceCircleIdentityId) cursor.delete();
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });
    await transactionDone(tx);
  } finally {
    db.close();
  }
}
